import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { LIST_WINDOWS, resolveWindow, type ListWindow } from '@monrdv/shared';
import { apiPath } from './tenant.js';
import { usePreferences } from './preferences.js';

/**
 * The state every list shares, and the rules for changing it.
 *
 * Six things are the same on factures, soumissions, travaux, clients and the journal: a text
 * filter, a period, an ordering, a page, a page size, and a way to take the whole result away as
 * a file. They were five separate implementations of two of those, which is why the invoice list
 * had no period at all and the customer list's pager always read "page 1 of 1".
 *
 * Two rules hold everywhere:
 *
 *   1. **It lives in the URL.** A coordinator sends a colleague "unpaid invoices over 30 days
 *      old, biggest first" as a link and it opens the same way. Nothing here is `useState`.
 *   2. **Changing a filter returns to page one.** Page 14 of the previous filter describes rows
 *      that are no longer in the result; keeping the cursor or the page number after a filter
 *      change lands somewhere arbitrary, and looks like data loss.
 */

/** `custom` is not a preference, only a URL state: two dates the user typed. */
export type WindowChoice = ListWindow | 'custom';

/** The parameters this hook owns. A route's own filters sit alongside them. */
export type ListSearchState = {
  q?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  window?: WindowChoice;
  from?: string;
  to?: string;
  dateField?: string;
  page?: number;
  pageSize?: number;
  cursor?: string;
};

export const DEFAULT_PAGE_SIZE = 50;

export type ListView = {
  q: string;
  sort: string;
  dir?: 'asc' | 'desc';
  /** The period in force, whether it came from the URL or from the user's preference. */
  window: WindowChoice;
  /** True when the period is the user's saved default rather than an explicit choice. */
  windowIsDefault: boolean;
  /**
   * Whether the saved default period applies to this list at all.
   *
   * False on the customer directory, which offers a period but never starts with one — so the
   * period panel there must not offer to pin a default it would then ignore.
   */
  appliesDefault: boolean;
  from?: string;
  to?: string;
  dateField: string;
  page: number;
  pageSize: number;
  /** Query string for the list request, period already resolved to two dates. */
  params: URLSearchParams;
  /** The same filters without paging — what the export asks for. */
  exportParams: URLSearchParams;
  /** Change one or more parameters. Anything but paging returns to page one. */
  set: (patch: Partial<ListSearchState>) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (sort: string, dir: 'asc' | 'desc' | undefined) => void;
  setWindow: (window: WindowChoice, from?: string, to?: string) => void;
  /** Save the period in force as the one every list opens on. */
  saveWindowAsDefault: () => void;
  savedDefaultWindow: ListWindow;
  savingDefault: boolean;
};

export function useListView(options: {
  /** The route id, for `useSearch`. */
  routeId: string;
  /** Applied when the URL names no sort. Must match the endpoint's own default. */
  defaultSort: string;
  /**
   * Whether a period makes sense here.
   *
   * False on the customer directory: a customer is not an event, and opening a directory of
   * 7 500 people on "the last 90 days" would hide almost all of them for no reason anybody
   * could guess from the screen.
   */
  timeBased: boolean;
  /** The endpoint's default date column, echoed so the toolbar can label the period. */
  defaultDateField?: string;
  /** Extra filters belonging to the screen, sent with both the list and the export. */
  extra?: Record<string, string | number | boolean | undefined>;
}): ListView {
  const { routeId, defaultSort, timeBased, defaultDateField = 'created', extra } = options;
  const navigate = useNavigate();
  const search = useSearch({ from: routeId as never }) as ListSearchState;
  const { preferences, update, isSaving } = usePreferences();

  /*
   * An absent `window` means "whatever I have set as my default", not "everything". The
   * preference is only ever the starting point: the moment somebody picks a period it is written
   * into the URL and the preference stops applying to that link.
   *
   * The period is stored symbolically — `window=30d`, not two dates — so a bookmark stays useful.
   * A saved link to "the last 30 days" that meant 1 May to 30 May forever would be stale within
   * the week. Specific dates are what `custom` is for, and those *are* stored literally.
   */
  const window: WindowChoice = search.window ?? (timeBased ? preferences.defaultListWindow : 'all');
  const windowIsDefault = timeBased && search.window === undefined;

  const range = useMemo(() => {
    if (window === 'custom') {
      return { from: search.from, to: search.to };
    }
    const resolved = resolveWindow(window);
    return { from: resolved?.from, to: resolved?.to };
  }, [window, search.from, search.to]);

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE;
  const sort = search.sort ?? defaultSort;
  const dateField = search.dateField ?? defaultDateField;

  const { params, exportParams } = useMemo(() => {
    const shared = new URLSearchParams();
    if (search.q?.trim()) shared.set('q', search.q.trim());
    shared.set('sort', sort);
    if (search.dir) shared.set('dir', search.dir);
    if (range.from) shared.set('from', range.from);
    if (range.to) shared.set('to', range.to);
    // Only worth sending when a period is actually in force; on its own it filters nothing and
    // would just be noise in every request log.
    if (range.from || range.to) shared.set('dateField', dateField);
    for (const [key, value] of Object.entries(extra ?? {})) {
      if (value === undefined || value === '' || value === false) continue;
      shared.set(key, String(value));
    }

    const paged = new URLSearchParams(shared);
    paged.set('page', String(page));
    paged.set('limit', String(pageSize));

    return { params: paged, exportParams: shared };
  }, [search.q, search.dir, sort, dateField, range.from, range.to, page, pageSize, extra]);

  const write = useCallback(
    (patch: Record<string, unknown>, keepPage: boolean) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          ...patch,
          // A cursor identifies a position in one particular result set. Carrying it into a
          // different query silently returns the wrong rows, so every write drops it.
          cursor: undefined,
          ...(keepPage ? null : { page: undefined }),
        }),
        // Replacing rather than pushing: adjusting three filters must not cost three presses of
        // the back button to leave the screen.
        replace: true,
      });
    },
    [navigate],
  );

  const set = useCallback((patch: Partial<ListSearchState>) => write(patch, false), [write]);

  return {
    q: search.q ?? '',
    sort,
    dir: search.dir,
    window,
    windowIsDefault,
    appliesDefault: timeBased,
    from: range.from,
    to: range.to,
    dateField,
    page,
    pageSize,
    params,
    exportParams,
    set,
    // Paging keeps the page it was given, obviously, and pushes nothing else around.
    setPage: useCallback((next: number) => write({ page: next === 1 ? undefined : next }, true), [write]),
    // Changing the size returns to page one: staying on page 40 of a list that just became a
    // quarter as long lands somewhere arbitrary.
    setPageSize: useCallback(
      (size: number) => write({ pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size }, false),
      [write],
    ),
    setSort: useCallback(
      (nextSort: string, nextDir: 'asc' | 'desc' | undefined) => write({ sort: nextSort, dir: nextDir }, false),
      [write],
    ),
    setWindow: useCallback(
      (nextWindow: WindowChoice, from?: string, to?: string) =>
        write(
          nextWindow === 'custom'
            ? { window: 'custom', from, to }
            : // The two dates only mean anything alongside `custom`; leaving them behind would
              // let a stale pair reappear the next time somebody picked specific dates.
              { window: nextWindow, from: undefined, to: undefined },
          false,
        ),
      [write],
    ),
    saveWindowAsDefault: useCallback(() => {
      // A specific pair of dates cannot be a default — it would go stale the day after it was
      // saved — so pinning a custom period is not offered, and this is a no-op if it happens.
      if (window === 'custom') return;
      update({ defaultListWindow: window });
    }, [update, window]),
    savedDefaultWindow: preferences.defaultListWindow,
    savingDefault: isSaving,
  };
}

/** The presets the period control offers, in the order they are shown. */
export const WINDOW_CHOICES: readonly ListWindow[] = LIST_WINDOWS;

export type ExportOutcome = {
  rows: number;
  matched: number;
  /** True when the row cap bit and the file is short of what the filters matched. */
  truncated: boolean;
};

/**
 * Fetch an export and hand it to the browser as a download.
 *
 * Done with `fetch` rather than a plain `<a download>` for two reasons that both matter: a link
 * cannot read the `x-export-truncated` header, so a capped file would arrive with nothing said
 * about it; and a link navigates, so a 403 would replace the application with a JSON error page
 * instead of showing a message on the screen the person is standing on.
 */
export async function downloadExport(path: string, params: URLSearchParams, locale: string): Promise<ExportOutcome> {
  const query = new URLSearchParams(params);
  query.set('locale', locale);

  const response = await fetch(`${apiPath(path)}?${query}`, {
    // The session is an httpOnly cookie, so the request must carry credentials like every other.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message ?? body?.error ?? String(response.status));
  }

  const blob = await response.blob();
  const filename = filenameFrom(response.headers.get('content-disposition')) ?? 'export.csv';

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Released on the next tick rather than immediately: revoking while the click is still being
  // processed cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);

  const rows = Number(response.headers.get('x-export-rows') ?? 0);
  const matched = Number(response.headers.get('x-export-matched') ?? rows);

  return { rows, matched, truncated: response.headers.get('x-export-truncated') === 'true' };
}

/** The server's chosen filename, preferring the encoded form so accents survive. */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header is not worth failing a download over; the plain form is next.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}
